import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import DataTable from "../../components/DataTable";
import { gtApi } from "../../api/generalTradeApi";
import { useFormat } from "../../../context/FormatContext";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useBusy } from "../../../context/BusyContext";
import "../../styles/generalTrade.css";

type DeliveryOrderStatus =
  | "draft"
  | "submitted"
  | "hold"
  | "cancelled"
  | "deleted";

interface SalesOrderRow {
  id: number;
  sales_order_number: string;
  order_date: string;
  delivery_date: string;
  company_name: string;
  grand_total: number | string;
  status: string;
  is_locked?: number | boolean;
  is_editable?: number | boolean;
  active_delivery_order_number?: string | null;
}

interface DeliveryOrderRow {
  id: number;
  delivery_order_number: string;
  sales_order_number: string;
  sales_order_id: number;
  delivery_date: string;
  company_name: string;
  grand_total: number | string;
  created_at: string;
  updated_at: string;
  status: DeliveryOrderStatus;
}

const today = (): string => new Date().toISOString().slice(0, 10);

export default function DeliveryOrders() {
  const { formatDateToDMY, formatDateOnly, formatCurrency } = useFormat();

  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();

  const [rows, setRows] = useState<DeliveryOrderRow[]>([]);
  const [orders, setOrders] = useState<SalesOrderRow[]>([]);
  const [selected, setSelected] = useState<DeliveryOrderRow | null>(null);

  const [importModalOpen, setImportModalOpen] = useState(false);

  const [selectedSalesOrder, setSelectedSalesOrder] =
    useState<SalesOrderRow | null>(null);

  const [salesOrderSearch, setSalesOrderSearch] = useState("");

  const [deliveryForm, setDeliveryForm] = useState({
    order_date: today(),
    delivery_date: today(),
    status: "submitted" as "draft" | "submitted",
    remarks: "",
  });

  const [creating, setCreating] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const [deliveryOrders, salesOrders] = await Promise.all([
        gtApi.deliveryOrders(),
        gtApi.salesOrders(),
      ]);

      setRows(Array.isArray(deliveryOrders) ? deliveryOrders : []);

      setOrders(Array.isArray(salesOrders) ? salesOrders : []);

      setSelected((previous) => {
        if (!previous || !Array.isArray(deliveryOrders)) {
          return null;
        }

        return (
          deliveryOrders.find(
            (row: DeliveryOrderRow) => Number(row.id) === Number(previous.id),
          ) || null
        );
      });
    } catch (error) {
      console.error("Failed to load Delivery Orders:", error);

      setRows([]);
      setOrders([]);
      setSelected(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  /*
   * A Sales Order is available for a new Delivery Order only when:
   * 1. The Sales Order is submitted.
   * 2. It has no Draft or Submitted Delivery Order.
   *
   * Hold, Cancelled and Deleted Delivery Orders do not lock the SO.
   */
  const availableSalesOrders = useMemo(() => {
    const keyword = salesOrderSearch.trim().toLowerCase();

    return orders
      .filter((order) => {
        const submitted =
          String(order.status || "").toLowerCase() === "submitted";

        const editable =
          Number(order.is_locked || 0) !== 1 &&
          Number(order.is_editable ?? 1) !== 0;

        return submitted && editable;
      })
      .filter((order) => {
        if (!keyword) {
          return true;
        }

        return [
          order.sales_order_number,
          order.company_name,
          order.order_date,
          order.grand_total,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(keyword),
        );
      });
  }, [orders, salesOrderSearch]);

  const deliveryColumns = [
    {
      key: "delivery_order_number",
      label: "DO No.",
    },
    {
      key: "sales_order_number",
      label: "SO No.",
    },
    {
      key: "delivery_date",
      label: "DO Date",
      render: (row: DeliveryOrderRow) =>
        row.delivery_date ? formatDateOnly(row.delivery_date) : "-",
    },
    {
      key: "company_name",
      label: "Customer",
    },
    {
      key: "grand_total",
      label: "Amount",
      render: (row: DeliveryOrderRow) =>
        formatCurrency(Number(row.grand_total || 0)),
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row: DeliveryOrderRow) =>
        row.created_at ? formatDateToDMY(row.created_at) : "-",
    },
    {
      key: "updated_at",
      label: "Updated At",
      render: (row: DeliveryOrderRow) =>
        row.updated_at ? formatDateToDMY(row.updated_at) : "-",
    },
    {
      key: "status",
      label: "Status",
      render: (row: DeliveryOrderRow) => {
        const status = String(row.status || "").toLowerCase();

        return (
          <span
            className={
              status === "submitted"
                ? "badge-success"
                : status === "draft" || status === "hold"
                  ? "badge-warning"
                  : "badge-danger"
            }
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
  ];

  const salesOrderColumns = [
    {
      key: "sales_order_number",
      label: "SO No.",
    },
    {
      key: "order_date",
      label: "SO Date",
      render: (row: SalesOrderRow) =>
        row.order_date ? formatDateOnly(row.order_date) : "-",
    },
    {
      key: "company_name",
      label: "Customer",
    },
    {
      key: "grand_total",
      label: "Amount",
      render: (row: SalesOrderRow) =>
        formatCurrency(Number(row.grand_total || 0)),
    },
    {
      key: "status",
      label: "Status",
      render: () => <span className="badge-success">Submitted</span>,
    },
  ];

  const openImportModal = () => {
    setSelectedSalesOrder(null);
    setSalesOrderSearch("");

    setDeliveryForm({
      order_date: today(),
      delivery_date: today(),
      status: "submitted",
      remarks: "",
    });

    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (creating) {
      return;
    }

    setImportModalOpen(false);
    setSelectedSalesOrder(null);
    setSalesOrderSearch("");
  };

  const handleSalesOrderSelect = (order: SalesOrderRow) => {
    setSelectedSalesOrder(order);

    setDeliveryForm((previous) => ({
      ...previous,
      order_date: String(order.order_date || "").slice(0, 10) || today(),
      delivery_date: String(order.delivery_date || "").slice(0, 10) || today(),
    }));
  };

  const createDeliveryOrder = async (): Promise<void> => {
    if (!selectedSalesOrder) {
      showModal({
        type: "error",
        title: "Sales Order Required",
        message: "Please select a Sales Order from the listing.",
        confirmText: "OK",
      });

      return;
    }

    if (!deliveryForm.order_date) {
      showModal({
        type: "error",
        title: "Order Date Required",
        message: "Please select the Delivery Order date.",
        confirmText: "OK",
      });

      return;
    }

    if (!deliveryForm.delivery_date) {
      showModal({
        type: "error",
        title: "Delivery Date Required",
        message: "Please select the delivery date.",
        confirmText: "OK",
      });

      return;
    }

    try {
      setCreating(true);

      const response = await runBusy(() =>
        gtApi.createDeliveryOrder({
          sales_order_id: Number(selectedSalesOrder.id),
          order_date: deliveryForm.order_date,
          delivery_date: deliveryForm.delivery_date,
          status: deliveryForm.status,
          remarks: deliveryForm.remarks.trim(),
        }),
      );

      setImportModalOpen(false);
      setSelectedSalesOrder(null);

      await load();

      showModal({
        type: "success",
        title: "Delivery Order Created",
        message: response?.message || "Delivery Order created successfully.",
        confirmText: "OK",
      });
    } catch (error: any) {
      console.error("Create Delivery Order Error:", error);

      showModal({
        type: "error",
        title: "Unable to Create",
        message:
          error?.response?.data?.message ||
          "Unable to create the Delivery Order.",
        confirmText: "OK",
      });
    } finally {
      setCreating(false);
    }
  };

  const changeStatus = (status: DeliveryOrderStatus) => {
    if (!selected) {
      showModal({
        type: "error",
        title: "Delivery Order Required",
        message: "Please select a Delivery Order first.",
        confirmText: "OK",
      });

      return;
    }

    const currentStatus = String(selected.status || "").toLowerCase();

    if (["cancelled", "deleted"].includes(currentStatus)) {
      showModal({
        type: "error",
        title: "Action Not Allowed",
        message: `A ${currentStatus} Delivery Order cannot be changed.`,
        confirmText: "OK",
      });

      return;
    }

    if (currentStatus === status) {
      showModal({
        type: "error",
        title: "No Status Change",
        message: `The Delivery Order is already ${status}.`,
        confirmText: "OK",
      });

      return;
    }

    const actionLabel =
      status === "deleted" ? "delete" : `change the status to ${status}`;

    showModal({
      type: "confirm",
      title:
        status === "deleted"
          ? "Delete Delivery Order"
          : "Change Delivery Order Status",
      message: `Are you sure you want to ${actionLabel} ${selected.delivery_order_number}?`,
      confirmText: status === "deleted" ? "Delete" : "Confirm",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const response = await runBusy(() =>
            gtApi.updateDeliveryOrderStatus(selected.id, status),
          );

          await load();
          setSelected(null);

          showModal({
            type: "success",
            title: "Success",
            message:
              response?.message ||
              `Delivery Order status changed to ${status}.`,
            confirmText: "OK",
          });
        } catch (error: any) {
          showModal({
            type: "error",
            title: "Unable to Update",
            message:
              error?.response?.data?.message ||
              "Unable to update the Delivery Order.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
<div className="gt-delivery-orders-page">
    <AdminListToolkit
      title="DELIVERY ORDERS"
      columns={deliveryColumns}
      data={rows}
      selectedRow={selected}
      onAdd={openImportModal}
      onView={(row) =>
        window.open(
          gtApi.pdfUrl("delivery-orders", row.id),
          "_blank",
        )
      }
      enableExport
      exportFileName="delivery-orders"
      extraButtons={[
        {
          label: "Actions ▾",
          onClick: () =>
            setActionsOpen((previous) => !previous),
        },
      ]}
    >
      {(filtered) => (
        <DataTable
          columns={deliveryColumns}
          data={filtered}
          selectedRowId={selected?.id}
          onRowSelect={(row) => {
            setSelected(row);
            setActionsOpen(false);
          }}
        />
      )}
    </AdminListToolkit>

    {actionsOpen && (
      <>
        <button
          type="button"
          className="gt-actions-backdrop"
          aria-label="Close actions"
          onClick={() => setActionsOpen(false)}
        />

        <div className="gt-actions-menu">
          <div className="gt-actions-menu-header">
            <div>
              <strong>Delivery Order Actions</strong>

              <span>
                {selected
                  ? selected.delivery_order_number
                  : "No Delivery Order selected"}
              </span>
            </div>

            <button
              type="button"
              className="gt-actions-menu-close"
              onClick={() => setActionsOpen(false)}
            >
              ×
            </button>
          </div>

          <button
            type="button"
            disabled={!selected}
            onClick={() => {
              setActionsOpen(false);
              changeStatus("submitted");
            }}
          >
            <span className="gt-action-icon">✓</span>

            <span>
              <strong>Submit</strong>
              <small>Mark Delivery Order as submitted</small>
            </span>
          </button>

          <button
            type="button"
            disabled={!selected}
            onClick={() => {
              setActionsOpen(false);
              changeStatus("hold");
            }}
          >
            <span className="gt-action-icon">Ⅱ</span>

            <span>
              <strong>Put on Hold</strong>
              <small>Temporarily release the Sales Order</small>
            </span>
          </button>

          <button
            type="button"
            disabled={!selected}
            className="gt-action-warning"
            onClick={() => {
              setActionsOpen(false);
              changeStatus("cancelled");
            }}
          >
            <span className="gt-action-icon">⊘</span>

            <span>
              <strong>Cancel Delivery Order</strong>
              <small>Cancel and release the Sales Order</small>
            </span>
          </button>

          <button
            type="button"
            disabled={!selected}
            className="gt-action-danger"
            onClick={() => {
              setActionsOpen(false);
              changeStatus("deleted");
            }}
          >
            <span className="gt-action-icon">×</span>

            <span>
              <strong>Delete Delivery Order</strong>
              <small>Soft-delete this document</small>
            </span>
          </button>

          <div className="gt-actions-menu-divider" />

          <button
            type="button"
            disabled={!selected}
            onClick={() => {
              if (!selected) return;

              setActionsOpen(false);

              window.open(
                gtApi.pdfUrl(
                  "delivery-orders",
                  selected.id,
                  "?prices=0",
                ),
                "_blank",
              );
            }}
          >
            <span className="gt-action-icon">▤</span>

            <span>
              <strong>Print Without Price</strong>
              <small>Open delivery report without amounts</small>
            </span>
          </button>
        </div>
      </>
    )}

      {importModalOpen && (
        <div
          className="gt-import-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Import Sales Order"
        >
          <div className="gt-import-modal">
            <div className="gt-import-modal-header">
              <div>
                <h2>IMPORT SALES ORDER</h2>

                <p>
                  Select a submitted Sales Order that does not have an active
                  Delivery Order.
                </p>
              </div>

              <button
                type="button"
                className="gt-import-modal-close"
                onClick={closeImportModal}
                disabled={creating}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="gt-import-modal-search">
              <input
                type="search"
                value={salesOrderSearch}
                placeholder="Search SO number, customer, date or amount..."
                onChange={(event) => setSalesOrderSearch(event.target.value)}
              />

              <span>
                {availableSalesOrders.length} record
                {availableSalesOrders.length === 1 ? "" : "s"} found
              </span>
            </div>

            <div className="gt-import-modal-table">
              {availableSalesOrders.length > 0 ? (
                <DataTable
                  columns={salesOrderColumns}
                  data={availableSalesOrders}
                  selectedRowId={selectedSalesOrder?.id}
                  onRowSelect={handleSalesOrderSelect}
                />
              ) : (
                <div className="gt-import-modal-empty">
                  No submitted Sales Orders are currently available for Delivery
                  Order creation.
                </div>
              )}
            </div>

            <div className="gt-import-selection">
              <div className="gt-import-selection-title">
                {selectedSalesOrder ? (
                  <>
                    Selected:{" "}
                    <strong>{selectedSalesOrder.sales_order_number}</strong> —{" "}
                    {selectedSalesOrder.company_name}
                  </>
                ) : (
                  "Select a Sales Order from the table above."
                )}
              </div>

              <div className="gt-import-form-grid">
                <label>
                  DO Date
                  <input
                    type="date"
                    value={deliveryForm.order_date}
                    disabled={!selectedSalesOrder || creating}
                    onChange={(event) =>
                      setDeliveryForm((previous) => ({
                        ...previous,
                        order_date: event.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Delivery Date
                  <input
                    type="date"
                    value={deliveryForm.delivery_date}
                    disabled={!selectedSalesOrder || creating}
                    onChange={(event) =>
                      setDeliveryForm((previous) => ({
                        ...previous,
                        delivery_date: event.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Initial Status
                  <select
                    value={deliveryForm.status}
                    disabled={!selectedSalesOrder || creating}
                    onChange={(event) =>
                      setDeliveryForm((previous) => ({
                        ...previous,
                        status: event.target.value as "draft" | "submitted",
                      }))
                    }
                  >
                    <option value="draft">Draft</option>

                    <option value="submitted">Submitted</option>
                  </select>
                </label>
              </div>

              <label className="gt-import-remarks">
                Remarks
                <textarea
                  value={deliveryForm.remarks}
                  disabled={!selectedSalesOrder || creating}
                  placeholder="Optional remarks"
                  onChange={(event) =>
                    setDeliveryForm((previous) => ({
                      ...previous,
                      remarks: event.target.value,
                    }))
                  }
                />
              </label>
            </div>

            <div className="gt-import-modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeImportModal}
                disabled={creating}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn-primary"
                disabled={!selectedSalesOrder || creating}
                onClick={() => void createDeliveryOrder()}
              >
                {creating ? "Creating..." : "Create Delivery Order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
